import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Fallback for callers that pin nothing against the bottom of the scrollport.
 * Anything that does pin should pass its own gutter; see the `gutterPx` docs.
 */
const DEFAULT_SCROLLPORT_GUTTER_PX = 12;
const RESPONSIVE_SCROLLPORT_QUERY = '(min-width: 768px)';

const scrollsVertically = (element: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(element);
  return (
    overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
  );
};

/**
 * Nearest ancestor that scrolls vertically, falling back to the document
 * element when the page itself is the scroller.
 */
const findScrollport = (node: HTMLElement): HTMLElement => {
  let candidate = node.parentElement;
  while (candidate) {
    if (scrollsVertically(candidate)) return candidate;
    candidate = candidate.parentElement;
  }
  return document.documentElement;
};

export interface ScrollportHeight {
  /**
   * Attach to any element inside the scrollport. The nearest scrolling ancestor
   * is resolved from it, so it does not have to be the pinned element itself.
   */
  ref: (node: HTMLElement | null) => void;
  /** Usable pixel height, or `null` before the first measurement lands. */
  height: number | null;
  /**
   * Returns the scrollport to its top. No-op until the ref has been attached.
   * Offered here because the caller has no other handle on the element: which
   * ancestor scrolls is resolved inside this hook.
   */
  scrollToTop: () => void;
}

/**
 * Measures how much vertical room a `position: sticky` element actually has.
 *
 * A pinned element taller than its scrollport can never be scrolled into view:
 * its top stays pinned and everything past the bottom edge is unreachable. The
 * fix is to cap the pinned element's height and let it scroll internally, which
 * needs the scrollport's height. That height cannot be expressed in CSS here
 * because which ancestor scrolls varies by how the panel is embedded, so it is
 * measured instead.
 *
 * Updates come only from `ResizeObserver` on the scrollport, which in practice
 * means window resizes. Scrolling never re-measures and never re-renders, so
 * dropping this into a long form does not make scrolling any more expensive.
 *
 * @param gutterPx Height held back from the measurement. Callers that pin an
 * element must pass at least the bottom padding of the content their scrollport
 * scrolls, because a sticky element is still bounded by its containing block:
 * once scrolling reaches the end, a box taller than the distance from the
 * scrollport's top edge to the bottom of that block is pushed *above* the top
 * edge rather than overflowing it, and its first rows are clipped. Padding on
 * the scrolled content ends that block early by exactly its own height.
 */
export const useScrollportHeight = (
  gutterPx: number = DEFAULT_SCROLLPORT_GUTTER_PX
): ScrollportHeight => {
  const [height, setHeight] = useState<number | null>(null);
  // The observed node lives in state, not a ref, so observing it happens in an
  // effect that owns both setup and teardown. Wiring the observer up in the ref
  // callback and tearing it down from a separate effect would leave it
  // disconnected for good the first time the two ran out of step, which is
  // exactly what StrictMode's remount does.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const ref = useCallback((node: HTMLElement | null) => setAnchor(node), []);
  // Held in a ref rather than state: exposing the element must not add a render.
  const scrollportRef = useRef<HTMLElement | null>(null);

  const scrollToTop = useCallback(() => {
    scrollportRef.current?.scrollTo({ top: 0 });
  }, []);

  // Layout effect rather than a passive one: the cap it produces is what keeps
  // an over-tall pinned panel inside the scrollport, so applying it before paint
  // avoids a frame of the panel at its full natural height.
  useLayoutEffect(() => {
    if (!anchor) {
      scrollportRef.current = null;
      return;
    }
    let observer: ResizeObserver | null = null;

    const measure = (scrollport: HTMLElement) => {
      const usable = scrollport.clientHeight - gutterPx;
      if (usable <= 0) return;
      // Skip no-op measurements so a resize that lands on the same height does
      // not re-render the form tree.
      setHeight(previous => (previous === usable ? previous : usable));
    };

    const observeCurrentScrollport = () => {
      observer?.disconnect();
      const scrollport = findScrollport(anchor);
      scrollportRef.current = scrollport;
      measure(scrollport);

      if (typeof ResizeObserver === 'undefined') return;
      observer = new ResizeObserver(() => measure(scrollport));
      observer.observe(scrollport);
    };

    observeCurrentScrollport();

    const mediaQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(RESPONSIVE_SCROLLPORT_QUERY)
        : null;
    mediaQuery?.addEventListener('change', observeCurrentScrollport);

    return () => {
      observer?.disconnect();
      mediaQuery?.removeEventListener('change', observeCurrentScrollport);
    };
  }, [anchor, gutterPx]);

  return { ref, height, scrollToTop };
};
